import { useMemo } from 'react';
import { Box, Typography, Avatar, Divider, Paper, Button, Stack, Autocomplete, Chip, TextField } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import ExploreOutlined from '@mui/icons-material/ExploreOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useOutletContext } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Client, Project, ProjectTeam, TeamRoleKey, User } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';
import ProjectProgramTimeline from './ProjectProgramTimeline';
import { TEAM_SECTIONS } from './wizard/teamSections';
import { buildTeamOptions, isRecommendedForRole } from './wizard/teamOptions';

export interface ProjectDetailEditState {
  draft: {
    sharepointLink: string;
    teamsLink: string;
    customerWebsite: string;
    team: ProjectTeam;
  };
  patch: (update: Partial<{
    sharepointLink: string;
    teamsLink: string;
    customerWebsite: string;
    team: ProjectTeam;
  }>) => void;
  allUsers: Array<{ id: string; data: User }>;
}

export interface ProjectDetailContext {
  project: Project & { id: string };
  client: Client | null;
  managers: User[];
  /**
   * Flattened, deduped list of all team users (managers + every role bucket).
   * Rendered as a single flat list in the Roles zone — no manager/member
   * sub-headings.
   */
  team: User[];
  /** Present when the current user can edit the project. Null for read-only viewers. */
  editState: ProjectDetailEditState | null;
}

/**
 * Overview tab — the default tab of ProjectDetail.
 *
 * Two-column grid on desktop, stacked on mobile with the program timeline
 * promoted above the roles list (mobile order: 1 = right stack, 2 = left stack)
 * so a long roles list never pushes the timeline below the fold.
 *
 * Left column (2fr):
 *   - Rôles / Équipe  — read-only 3-column grid OR inline team editor when editState is present
 *
 * Right column (1fr):
 *   - Historique des programmes — vertical scrollable timeline (always read-only)
 *   - Liens           — read-only anchor links OR inline text fields when editState is present
 */
export default function ProjectOverview() {
  const { t } = useTranslation();
  const { project, team, editState } = useOutletContext<ProjectDetailContext>();

  return (
    <Box
      data-testid="project-overview"
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: '6fr 1fr' },
        gridTemplateRows: 'auto 1fr',
      }}
    >
      {/* Row 1, Left — Links */}
      <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
        {editState ? (
          <Zone title={t('projects.detail.links')}>
            <Stack spacing={1.5}>
              <TextField
                label="SharePoint"
                value={editState.draft.sharepointLink}
                onChange={(e) => editState.patch({ sharepointLink: e.target.value })}
                size="small"
                fullWidth
                placeholder="https://..."
              />
              <TextField
                label="Teams"
                value={editState.draft.teamsLink}
                onChange={(e) => editState.patch({ teamsLink: e.target.value })}
                size="small"
                fullWidth
                placeholder="https://..."
              />
              <TextField
                label={t('projects.detail.customerWebsite')}
                value={editState.draft.customerWebsite}
                onChange={(e) => editState.patch({ customerWebsite: e.target.value })}
                size="small"
                fullWidth
                placeholder="https://..."
              />
            </Stack>
          </Zone>
        ) : (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <LinkButton
              label={t('projects.detail.openSharepoint')}
              href={project.sharepointLink}
              icon={<SharePointIcon />}
              brandColor="#038387"
            />
            <LinkButton
              label={t('projects.detail.openTeams')}
              href={project.teamsLink}
              icon={<TeamsIcon />}
              brandColor="#5B5FC7"
            />
            <LinkButton
              label={t('projects.detail.visitWebsite')}
              href={project.customerWebsite}
              icon={<LanguageIcon sx={{ fontSize: 18 }} />}
              brandColor={colors.bodyText}
            />
          </Stack>
        )}
      </Box>

      {/* Row 1+2, Right — Project life timeline (spans both rows) */}
      <Box sx={{ gridColumn: { md: 2 }, gridRow: { md: '1 / -1' } }}>
        <ProjectProgramTimeline phases={project.programHistory ?? []} />
      </Box>

      {/* Row 2, Left — Team */}
      <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 } }}>
        {editState ? (
          <Zone title={t('projects.wizard.stepTeam', 'Équipe')}>
            <TeamEditor
              team={editState.draft.team}
              users={editState.allUsers}
              onChange={(newTeam) => editState.patch({ team: newTeam })}
            />
          </Zone>
        ) : (
          <TeamBento projectTeam={project.team} users={team} />
        )}
      </Box>
    </Box>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function Zone({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper
      variant="outlined"
      elevation={0}
      sx={{
        p: 2,
        borderColor: colors.lightGray,
        borderRadius: 0,
      }}
    >
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function LinkButton({
  label,
  href,
  icon,
  brandColor,
}: {
  label: string;
  href?: string;
  icon: React.ReactNode;
  brandColor: string;
}) {
  if (!href) return null;
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={icon}
      sx={{
        justifyContent: 'flex-start',
        bgcolor: brandColor,
        color: '#fff',
        px: 2,
        py: 0.75,
        '&:hover': {
          bgcolor: brandColor,
          filter: 'brightness(0.85)',
        },
      }}
    >
      {label}
    </Button>
  );
}

function SharePointIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="6" fill="#fff" fillOpacity={0.9} />
      <circle cx="7" cy="14" r="5" fill="#fff" fillOpacity={0.7} />
      <circle cx="13" cy="17" r="4" fill="#fff" fillOpacity={0.5} />
    </svg>
  );
}

function TeamsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16.5 3a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm3 6h-4a1 1 0 0 0-1 1v5a3 3 0 0 0 6 0v-5a1 1 0 0 0-1-1ZM10 4a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm4 8H4a1 1 0 0 0-1 1v4a5 5 0 0 0 10 0v-4a1 1 0 0 0-1-1Z" />
    </svg>
  );
}

// ─── Bento grid — team showcase with varied card sizes ──────────────────────

/** Background icon per role family */
const FAMILY_ICONS: Record<string, React.ComponentType<SvgIconProps>> = {
  leaders: ExploreOutlined,
  technical: SettingsOutlined,
  quality: ShieldOutlined,
  operations: LocalShippingOutlined,
  growth: CampaignOutlined,
  contributors: GroupsOutlined,
};


/** Per-role-key label + tagline for individual cards */
const ROLE_LABEL_KEYS: Partial<Record<TeamRoleKey, { label: string; tagline: string }>> = {
  rdLead: { label: 'projects.detail.bento.rdLead', tagline: 'projects.detail.bento.rdLeadTag' },
  rdMeca: { label: 'projects.detail.bento.rdMeca', tagline: 'projects.detail.bento.rdMecaTag' },
  rdElec: { label: 'projects.detail.bento.rdElec', tagline: 'projects.detail.bento.rdElecTag' },
  testBench: { label: 'projects.detail.bento.testBench', tagline: 'projects.detail.bento.testBenchTag' },
  supplyChain: { label: 'projects.detail.bento.supplyChain', tagline: 'projects.detail.bento.supplyChainTag' },
};

interface BentoSlot {
  family: string;
  keys: TeamRoleKey[];
  variant: 'wide' | 'tall' | 'individual';
  labelKey: string;
  taglineKey: string;
  cardSx?: Record<string, unknown>;
}

const BENTO_SLOTS: BentoSlot[] = [
  { family: 'leaders', keys: ['managers'], variant: 'wide', labelKey: 'projects.detail.bento.captains', taglineKey: 'projects.detail.bento.captainsTag', cardSx: { bgcolor: colors.yellow, borderColor: colors.yellowHover } },
  { family: 'quality', keys: ['quality', 'dft'], variant: 'tall', labelKey: 'projects.detail.bento.quality', taglineKey: 'projects.detail.bento.qualityTag' },
  { family: 'technical', keys: ['rdLead', 'rdMeca', 'rdElec', 'testBench'], variant: 'individual', labelKey: '', taglineKey: '' },
  { family: 'operations', keys: ['supplyChain'], variant: 'individual', labelKey: '', taglineKey: '' },
  { family: 'growth', keys: ['bizdev', 'marketing'], variant: 'wide', labelKey: 'projects.detail.bento.growth', taglineKey: 'projects.detail.bento.growthTag' },
  { family: 'contributors', keys: ['contributors'], variant: 'wide', labelKey: 'projects.detail.bento.contributors', taglineKey: 'projects.detail.bento.contributorsTag' },
];

function TeamBento({ projectTeam, users }: { projectTeam: ProjectTeam; users: User[] }) {
  const { t } = useTranslation();
  const byUid = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of users) m.set(u.uid, u);
    return m;
  }, [users]);

  if (users.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('projects.detail.noRoles')}
        </Typography>
      </Box>
    );
  }

  /** Resolve uids → User[] for a set of role keys, deduped */
  const resolve = (keys: TeamRoleKey[]) => {
    const seen = new Set<string>();
    const result: { user: User; roleKey: TeamRoleKey }[] = [];
    for (const key of keys) {
      for (const uid of projectTeam[key] ?? []) {
        if (!seen.has(uid)) {
          const u = byUid.get(uid);
          if (u) { result.push({ user: u, roleKey: key }); seen.add(uid); }
        }
      }
    }
    return result;
  };

  return (
    <Box data-testid="team-showcase">
      {/* Section header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t('projects.detail.bento.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
          {t('projects.detail.bento.subtitle')}
        </Typography>
      </Box>

      {/* Bento grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gridAutoFlow: 'dense',
          gap: 1.5,
        }}
      >
        {BENTO_SLOTS.map((slot) => {
          const members = resolve(slot.keys);
          if (members.length === 0) return null;
          const BgIcon = FAMILY_ICONS[slot.family];

          if (slot.variant === 'individual') {
            // Group members by roleKey so people with the same role share one card
            const byRole = new Map<TeamRoleKey, User[]>();
            for (const { user: u, roleKey } of members) {
              const arr = byRole.get(roleKey) ?? [];
              arr.push(u);
              byRole.set(roleKey, arr);
            }
            // Smart bento sizing: alternate between wide/tall for multi-person
            // groups to create visual rhythm. Singles stay 1x1.
            let multiIdx = 0;
            return Array.from(byRole.entries()).map(([roleKey, roleUsers]) => {
              const rlk = ROLE_LABEL_KEYS[roleKey];
              const count = roleUsers.length;

              // 1 person → standard 1x1
              if (count === 1) {
                return (
                  <BentoCard
                    key={roleKey}
                    bgIcon={BgIcon}
                    label={rlk ? t(rlk.label) : undefined}
                    tagline={rlk ? t(rlk.tagline) : undefined}
                  >
                    <PersonBlock user={roleUsers[0]} />
                  </BentoCard>
                );
              }

              // 2+ people → alternate wide (2 cols) / tall (2 rows) for variety
              const useWide = multiIdx % 2 === 0;
              multiIdx++;

              if (useWide) {
                // Wide card: people side by side with dividers
                return (
                  <BentoCard
                    key={roleKey}
                    sx={{ gridColumn: { sm: 'span 2' } }}
                    bgIcon={BgIcon}
                    label={rlk ? t(rlk.label) : undefined}
                    tagline={rlk ? t(rlk.tagline) : undefined}
                  >
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      {roleUsers.map((u, i) => (
                        <Box key={u.uid} sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                          {i > 0 && (
                            <Divider orientation="vertical" flexItem sx={{ borderColor: colors.lightGray, mr: 0 }} />
                          )}
                          <PersonBlock user={u} horizontal />
                        </Box>
                      ))}
                    </Box>
                  </BentoCard>
                );
              }

              // Tall card: people stacked vertically
              return (
                <BentoCard
                  key={roleKey}
                  sx={{ gridRow: { md: 'span 2' } }}
                  bgIcon={BgIcon}
                  label={rlk ? t(rlk.label) : undefined}
                  tagline={rlk ? t(rlk.tagline) : undefined}
                >
                  <Stack spacing={2}>
                    {roleUsers.map((u) => (
                      <PersonBlock key={u.uid} user={u} horizontal />
                    ))}
                  </Stack>
                </BentoCard>
              );
            });
          }

          if (slot.variant === 'wide') {
            const isCaptain = slot.family === 'leaders';
            return (
              <BentoCard
                key={slot.family}
                sx={{ gridColumn: { sm: 'span 2' }, ...slot.cardSx }}
                bgIcon={BgIcon}
                label={t(slot.labelKey)}
                tagline={t(slot.taglineKey)}
                featured={isCaptain}
              >
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {members.map(({ user: u }, i) => (
                    <Box key={u.uid} sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      {i > 0 && (
                        <Divider orientation="vertical" flexItem sx={{ borderColor: colors.lightGray, mr: 0 }} />
                      )}
                      <PersonBlock user={u} horizontal large={isCaptain} />
                    </Box>
                  ))}
                </Box>
              </BentoCard>
            );
          }

          /* tall */
          return (
            <BentoCard
              key={slot.family}
              sx={{ gridRow: { md: 'span 2' } }}
              bgIcon={BgIcon}
              label={t(slot.labelKey)}
              tagline={t(slot.taglineKey)}
            >
              <Stack spacing={2.5}>
                {members.map(({ user: u }) => (
                  <PersonBlock key={u.uid} user={u} />
                ))}
              </Stack>
            </BentoCard>
          );
        })}
      </Box>
    </Box>
  );
}

/** A single bento card with background decorative icon */
function BentoCard({
  children,
  bgIcon: BgIcon,
  label,
  tagline,
  featured,
  sx: sxOverride,
}: {
  children: React.ReactNode;
  bgIcon: React.ComponentType<SvgIconProps>;
  label?: string;
  tagline?: string;
  featured?: boolean;
  sx?: Record<string, unknown>;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: 3,
        borderColor: colors.lightGray,
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          transform: 'translateY(-2px)',
        },
        ...sxOverride,
      }}
    >
      {/* Large background icon */}
      <BgIcon
        sx={{
          position: 'absolute',
          top: -16,
          right: -16,
          fontSize: 150,
          color: colors.bodyText,
          opacity: 0.04,
          pointerEvents: 'none',
        }}
      />

      {/* Section label */}
      {label && (
        <Typography
          variant={featured ? 'body1' : 'caption'}
          sx={{
            display: 'block',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: colors.textMuted,
            fontSize: featured ? 17 : undefined,
            mb: 2,
            position: 'relative',
          }}
        >
          {label}
          {tagline && (
            <Typography
              component="span"
              variant={featured ? 'body2' : 'caption'}
              sx={{ fontWeight: 400, fontStyle: 'italic', ml: 0.75 }}
            >
              — {tagline}
            </Typography>
          )}
        </Typography>
      )}

      {/* Content */}
      <Box sx={{ position: 'relative' }}>{children}</Box>
    </Paper>
  );
}

/** Avatar + name + yellow job title for one person */
function PersonBlock({
  user,
  horizontal,
  large,
}: {
  user: User;
  horizontal?: boolean;
  large?: boolean;
}) {
  const initials = user.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const avatarSize = large ? 100 : 56;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: horizontal ? 'row' : 'column',
        alignItems: horizontal ? 'center' : 'flex-start',
        gap: horizontal ? 2 : 0,
      }}
    >
      <Avatar
        src={user.photoURL ?? undefined}
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: '50%',
          fontSize: large ? '26px' : '18px',
          fontWeight: 700,
          bgcolor: colors.peach,
          color: colors.bodyText,
          mb: horizontal ? 0 : 1.5,
        }}
      >
        {initials}
      </Avatar>
      <Box>
        <Typography
          variant={large ? 'h6' : 'body2'}
          sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: large ? 20 : undefined }}
        >
          {user.displayName}
        </Typography>
        {user.jobTitle && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              lineHeight: 1.4,
              color: colors.bodyText,
              bgcolor: colors.yellow,
              px: 0.75,
              py: 0.15,
              mt: 0.25,
              width: 'fit-content',
              fontWeight: 600,
              fontSize: large ? 14 : undefined,
            }}
          >
            {user.jobTitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * Inline team editor used in the Overview edit mode.
 * Renders one Autocomplete per role bucket, mirroring StepTeam but without wizard chrome.
 */
function TeamEditor({
  team,
  users,
  onChange,
}: {
  team: ProjectTeam;
  users: Array<{ id: string; data: User }>;
  onChange: (team: ProjectTeam) => void;
}) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {TEAM_SECTIONS.map((section) => {
        const options = buildTeamOptions(users, section.filterRole);
        const selectedUids = team[section.key] ?? [];
        const selectedUsers = selectedUids
          .map((uid) => users.find((u) => u.id === uid))
          .filter(Boolean) as Array<{ id: string; data: User }>;

        return (
          <Box key={section.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t(section.labelKey, section.key)}
              </Typography>
              {section.required && (
                <Chip
                  label="*"
                  size="small"
                  sx={{ bgcolor: colors.yellow, color: '#000', fontSize: 10, height: 16, minWidth: 16 }}
                />
              )}
            </Box>
            <Autocomplete
              multiple
              size="small"
              options={options}
              value={selectedUsers}
              onChange={(_e, newValue) => {
                onChange({ ...team, [section.key]: newValue.map((u) => u.id) });
              }}
              getOptionLabel={(opt) => opt.data.displayName}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              groupBy={
                section.filterRole === null
                  ? undefined
                  : (opt) =>
                      isRecommendedForRole(opt, section.filterRole)
                        ? t('projects.wizard.team.recommended', 'Recommended')
                        : t('projects.wizard.team.others', 'Others')
              }
              filterOptions={(opts, state) => {
                const q = state.inputValue.toLowerCase();
                if (!q) return opts;
                return opts.filter(
                  (o) =>
                    (o.data.displayName ?? '').toLowerCase().includes(q) ||
                    (o.data.jobTitle ?? '').toLowerCase().includes(q),
                );
              }}
              slotProps={{ chip: { size: 'small' } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={t('projects.wizard.userSearchPlaceholder', 'Search by name or job title...')}
                />
              )}
            />
          </Box>
        );
      })}
    </Box>
  );
}
